/**
 * offer-batch-modals.ts
 * Batch Create / Edit modals for Offer Units and Prices.
 */

import { showNotification, escapeHtml } from '../lib/helpers';
import { fetchJson, postJson } from '../lib/api-client';

// ── Group-name constants ────────────────────────────────────────────────────
const G_INSURANCE     = 'Insurance';
const G_TRANSPORT     = 'Transport';
const G_ACCOMMODATION = 'Accommodation';

// ── Response interfaces ─────────────────────────────────────────────────────

interface UnitFormDataResponse {
    success: boolean;
    offerGroupName: string;
    agencyAcceptsPartnership: boolean;
    unitTypes: Array<{ id: number; name: string }>;
    classifications: Array<{ key: string; name: string }>;
    services: Array<{ key: string; name: string }>;
}

// ── Public interfaces ───────────────────────────────────────────────────────

export interface UnitItem {
    name: string;
    offerUnitTypeId: number;
    offerUnitTypeName: string;
    classification: string;
    service: string;
    active: boolean;
    groupData: Record<string, string>;
}

export interface PriceItem {
    price: number;
    partnerPriceOverride: number;
    from: string;
    to: string;
    status: number;
    numberOfSeats: number | null;
}

export interface BatchModalConfig {
    urlCreateUnits: string;
    urlUnitFormData: string;
}

// ── Shared form helpers ─────────────────────────────────────────────────────

async function loadUnitFormData(
    url: string,
    offerId: number,
    cache: Record<number, UnitFormDataResponse>
): Promise<UnitFormDataResponse | null> {
    if (cache[offerId]) return cache[offerId];
    try {
        const data = await fetchJson<UnitFormDataResponse>(`${url}?offerId=${offerId}`);
        if (data.success) {
            cache[offerId] = data;
            return data;
        }
        return null;
    } catch (err) {
        console.error('Failed to load unit form data:', err);
        return null;
    }
}

function populateUnitSelects(
    typeSelect: HTMLSelectElement,
    classSelect: HTMLSelectElement,
    serviceSelect: HTMLSelectElement,
    data: UnitFormDataResponse
): void {
    typeSelect.innerHTML = '';
    for (const t of data.unitTypes) {
        typeSelect.add(new Option(t.name, String(t.id)));
    }
    classSelect.innerHTML = '';
    for (const c of data.classifications) {
        classSelect.add(new Option(c.name, c.key));
    }
    serviceSelect.innerHTML = '';
    for (const s of data.services) {
        serviceSelect.add(new Option(s.name, s.key));
    }
}

interface UnitVisibilityElements {
    classGroup: HTMLElement;
    serviceGroup: HTMLElement;
    numDaysGroup: HTMLElement;
    insurancePriceGroup: HTMLElement;
    insurancePartnerGroup: HTMLElement | null;
}

function applyUnitGroupVisibility(
    offerGroup: string,
    els: UnitVisibilityElements,
    agencyAcceptsPartnership: boolean,
    showClassAndService: boolean
): void {
    const isInsurance = offerGroup === G_INSURANCE;
    els.classGroup.style.display           = (!isInsurance && showClassAndService) ? '' : 'none';
    els.serviceGroup.style.display         = (!isInsurance && showClassAndService) ? '' : 'none';
    els.numDaysGroup.style.display         = isInsurance ? '' : 'none';
    els.insurancePriceGroup.style.display  = isInsurance ? '' : 'none';
    if (els.insurancePartnerGroup) {
        els.insurancePartnerGroup.style.display = (isInsurance && agencyAcceptsPartnership) ? '' : 'none';
    }
}

// ═══════════════════════════════════════════
//  BATCH CREATE OFFER UNITS — spreadsheet grid
// ═══════════════════════════════════════════

type UnitGridRow = {
    id: number;
    name: string;
    offerUnitTypeId: number;
    offerUnitTypeName: string;
    classification: string;
    service: string;
    active: boolean;
    numberOfDays: string;
    price: string;
    partnerPriceOverride: string;
};

export function initUnitModal(config: BatchModalConfig & {
    fixedOfferId?: number;
    fixedOfferName?: string;
    fixedOfferGroup?: string;
    capsShowClassification?: boolean;
    capsShowService?: boolean;
}) {
    const unitModal = document.getElementById('createUnitModal');
    if (!unitModal) return;

    // UI elements
    const step1El        = document.getElementById('unit-step-1')!;
    const step2El        = document.getElementById('unit-step-2')!;
    const loadingEl      = document.getElementById('unit-form-loading')!;
    const tbody          = document.getElementById('units-grid-tbody')!;
    const reviewTbody    = document.getElementById('unit-review-tbody')!;
    const reviewBtn      = document.getElementById('unit-review-btn') as HTMLButtonElement;
    const backBtn        = document.getElementById('unit-back-btn') as HTMLButtonElement;
    const submitBtn      = document.getElementById('unit-submit-all-btn') as HTMLButtonElement;
    const hintEl         = document.getElementById('unit-row-count-hint')!;
    const reviewSummary  = document.getElementById('unit-review-summary')!;
    const offerNameEl    = document.getElementById('unit-offer-name')!;
    const addRowBtn      = document.getElementById('unit-add-row')!;
    const step2Indicator = document.getElementById('bulk-step-2-indicator');
    const grid           = document.getElementById('units-grid')!;

    // State
    let rows: UnitGridRow[] = [];
    let nextId = 1;
    let currentOfferId    = config.fixedOfferId ?? 0;
    let currentOfferGroup = config.fixedOfferGroup ?? '';
    let agencyAcceptsPartnership = true;
    const formDataCache: Record<number, UnitFormDataResponse> = {};

    const locYes      = () => unitModal.dataset.locYes      ?? 'Yes';
    const locNo       = () => unitModal.dataset.locNo       ?? 'No';
    const locReview   = () => unitModal.dataset.locReview   ?? 'Review';
    const locSaveUnits = () => unitModal.dataset.locSaveUnits ?? 'Save units';

    // Option templates (built once per open, cloned into each select)
    let typeOptions: Option[] = [];
    let classOptions: Option[] = [];
    let serviceOptions: Option[] = [];

    function blankRow(): UnitGridRow {
        return { id: nextId++, name: '', offerUnitTypeId: 0, offerUnitTypeName: '', classification: '', service: '', active: true, numberOfDays: '', price: '', partnerPriceOverride: '' };
    }

    function isInsurance(): boolean { return currentOfferGroup === G_INSURANCE; }

    function filledRows(): UnitGridRow[] {
        return rows.filter(r => r.name.trim() !== '' && r.offerUnitTypeId > 0);
    }

    // ── Column visibility ──────────────────────────────
    function applyColumnVisibility(): void {
        const ins = isInsurance();
        grid.querySelectorAll('.col-class, .col-review-class').forEach(el => el.classList.toggle('d-none', ins));
        grid.querySelectorAll('.col-service, .col-review-service').forEach(el => el.classList.toggle('d-none', ins));
        grid.querySelectorAll('.col-days, .col-review-days').forEach(el => el.classList.toggle('d-none', !ins));
        grid.querySelectorAll('.col-price, .col-review-price').forEach(el => el.classList.toggle('d-none', !ins));
        grid.querySelectorAll('.col-partner').forEach(el => el.classList.toggle('d-none', !ins || !agencyAcceptsPartnership));
    }

    // ── Row rendering ──────────────────────────────────
    // Use defaultSelected (3rd arg) so the HTML attribute is written and innerHTML
    // serialisation preserves the selection if the row is ever serialised.
    function makeSelect(opts: Option[], currentVal: string): HTMLSelectElement {
        const sel = document.createElement('select');
        sel.className = 'form-select form-select-sm';
        const blankSel = currentVal === '0' || currentVal === '';
        sel.add(new Option('—', '0', blankSel, blankSel));
        opts.forEach(o => {
            const isSel = o.value === currentVal;
            sel.add(new Option(o.text, o.value, isSel, isSel));
        });
        return sel;
    }

    function renderRow(row: UnitGridRow, idx: number): HTMLTableRowElement {
        const tr = document.createElement('tr');
        tr.dataset.rowId = String(row.id);
        if (row.name.trim()) tr.classList.add('row-filled');

        const ins = isInsurance();
        const typeId = row.offerUnitTypeId > 0 ? String(row.offerUnitTypeId) : '0';

        // Build the entire row as a single innerHTML string — leave select columns as
        // empty placeholder <td>s so no innerHTML serialisation of <select> ever occurs.
        tr.innerHTML = `
            <td class="col-num text-muted">${idx + 1}</td>
            <td class="col-name"><input type="text" class="form-control form-control-sm cell-name" value="${escapeHtml(row.name)}" placeholder="Name..."/></td>
            <td class="col-type"></td>
            <td class="col-class${ins ? ' d-none' : ''}"></td>
            <td class="col-service${ins ? ' d-none' : ''}"></td>
            <td class="col-days${ins ? '' : ' d-none'}"><input type="number" class="form-control form-control-sm cell-days" value="${escapeHtml(row.numberOfDays)}" min="1" placeholder="0"/></td>
            <td class="col-price${ins ? '' : ' d-none'}"><input type="number" class="form-control form-control-sm cell-price" value="${escapeHtml(row.price)}" min="0" step="0.01" placeholder="0.00"/></td>
            <td class="col-partner${ins && agencyAcceptsPartnership ? '' : ' d-none'}"><input type="number" class="form-control form-control-sm cell-partner" value="${escapeHtml(row.partnerPriceOverride)}" min="0" step="0.01" placeholder="0.00"/></td>
            <td class="col-active text-center"><input type="checkbox" class="form-check-input cell-active" ${row.active ? 'checked' : ''}/></td>
            <td class="col-actions"><div class="row-actions d-flex gap-1">
                <button type="button" class="btn-copy-row" title="Copy row">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="13" height="13" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75" /></svg>
                </button>
                <button type="button" class="btn-delete-row" title="Delete row">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="13" height="13" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>
                </button>
            </div></td>`;

        // Append selects into their placeholder cells — never use innerHTML += after this point.
        tr.querySelector('.col-type')!.appendChild(makeSelect(typeOptions, typeId));
        tr.querySelector('.col-class')!.appendChild(makeSelect(classOptions, row.classification));
        tr.querySelector('.col-service')!.appendChild(makeSelect(serviceOptions, row.service));

        return tr;
    }

    function renderGrid(): void {
        // Preserve focused row id so we can restore focus
        const focusedInput = tbody.querySelector('input:focus, select:focus') as HTMLElement | null;
        const focusedRowId = focusedInput?.closest('tr')?.dataset.rowId;
        const focusedClass = focusedInput?.className.split(' ').find(c => c.startsWith('cell-'));

        tbody.innerHTML = '';
        rows.forEach((row, idx) => {
            tbody.appendChild(renderRow(row, idx));
        });

        // Re-focus
        if (focusedRowId && focusedClass) {
            const tr = tbody.querySelector(`tr[data-row-id="${focusedRowId}"]`);
            (tr?.querySelector(`.${focusedClass}`) as HTMLElement | null)?.focus();
        }

        updateHint();
    }

    function updateHint(): void {
        const filled = filledRows().length;
        hintEl.textContent = `${filled} / ${rows.length}`;
        reviewBtn.disabled = filled === 0;
        const rev = locReview();
        reviewBtn.textContent = filled > 0 ? `${rev} (${filled}) →` : `${rev} →`;
        submitBtn.textContent = `✓ ${locSaveUnits()} (${filled})`;
    }

    // ── Read row state from DOM (called on every cell change) ──
    function syncRowFromDom(tr: HTMLTableRowElement): void {
        const id = parseInt(tr.dataset.rowId ?? '0', 10);
        const row = rows.find(r => r.id === id);
        if (!row) return;

        const nameInput   = tr.querySelector('.cell-name') as HTMLInputElement | null;
        const typeSelect  = tr.querySelector('.col-type select') as HTMLSelectElement | null;
        const classSelect = tr.querySelector('.col-class select') as HTMLSelectElement | null;
        const svcSelect   = tr.querySelector('.col-service select') as HTMLSelectElement | null;
        const daysInput   = tr.querySelector('.cell-days') as HTMLInputElement | null;
        const priceInput  = tr.querySelector('.cell-price') as HTMLInputElement | null;
        const partnerInput = tr.querySelector('.cell-partner') as HTMLInputElement | null;
        const activeChk   = tr.querySelector('.cell-active') as HTMLInputElement | null;

        if (nameInput)   row.name = nameInput.value;
        if (typeSelect) {
            row.offerUnitTypeId = parseInt(typeSelect.value, 10) || 0;
            row.offerUnitTypeName = typeSelect.options[typeSelect.selectedIndex]?.text ?? '';
        }
        if (classSelect) row.classification = classSelect.value === '0' ? '' : classSelect.value;
        if (svcSelect)   row.service = svcSelect.value === '0' ? '' : svcSelect.value;
        if (daysInput)   row.numberOfDays = daysInput.value;
        if (priceInput)  row.price = priceInput.value;
        if (partnerInput) row.partnerPriceOverride = partnerInput.value;
        if (activeChk)   row.active = activeChk.checked;

        tr.classList.toggle('row-filled', row.name.trim() !== '' && row.offerUnitTypeId > 0);
        updateHint();
    }

    // ── Event delegation on tbody ──────────────────────
    tbody.addEventListener('input', (e) => {
        const tr = (e.target as HTMLElement).closest('tr') as HTMLTableRowElement | null;
        if (tr) syncRowFromDom(tr);
    });
    tbody.addEventListener('change', (e) => {
        const tr = (e.target as HTMLElement).closest('tr') as HTMLTableRowElement | null;
        if (tr) syncRowFromDom(tr);
    });

    tbody.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;

        // Copy row
        if (target.closest('.btn-copy-row')) {
            const tr = target.closest('tr') as HTMLTableRowElement;
            syncRowFromDom(tr);
            const id = parseInt(tr.dataset.rowId ?? '0', 10);
            const srcIdx = rows.findIndex(r => r.id === id);
            if (srcIdx !== -1) {
                const clone = { ...rows[srcIdx], id: nextId++ };
                rows.splice(srcIdx + 1, 0, clone);
                renderGrid();
            }
            return;
        }

        // Delete row
        if (target.closest('.btn-delete-row')) {
            const tr = target.closest('tr') as HTMLTableRowElement;
            const id = parseInt(tr.dataset.rowId ?? '0', 10);
            rows = rows.filter(r => r.id !== id);
            if (rows.length === 0) rows.push(blankRow());
            renderGrid();
            return;
        }
    });

    // Add row
    addRowBtn.addEventListener('click', () => {
        rows.push(blankRow());
        renderGrid();
        // Focus the name input of the new row
        const lastTr = tbody.lastElementChild as HTMLTableRowElement | null;
        (lastTr?.querySelector('.cell-name') as HTMLInputElement | null)?.focus();
    });

    // ── Step navigation ───────────────────────────────
    reviewBtn.addEventListener('click', () => {
        const filled = filledRows();
        if (!filled.length) return;
        step1El.classList.add('d-none');
        step2El.classList.remove('d-none');
        reviewBtn.classList.add('d-none');
        submitBtn.classList.remove('d-none');
        backBtn.classList.remove('d-none');
        step2Indicator?.classList.add('active');
        renderReview(filled);
    });

    backBtn.addEventListener('click', () => {
        step2El.classList.add('d-none');
        step1El.classList.remove('d-none');
        submitBtn.classList.add('d-none');
        reviewBtn.classList.remove('d-none');
        backBtn.classList.add('d-none');
        step2Indicator?.classList.remove('active');
    });

    // ── Review table ──────────────────────────────────
    function renderReview(filled: UnitGridRow[]): void {
        reviewSummary.textContent = `${filled.length} unit${filled.length === 1 ? '' : 's'} ready to save.`;
        reviewTbody.innerHTML = '';
        filled.forEach((u, i) => {
            const tr = document.createElement('tr');
            tr.innerHTML = `
                <td>${i + 1}</td>
                <td>${escapeHtml(u.name)}</td>
                <td>${escapeHtml(u.offerUnitTypeName)}</td>
                <td class="col-review-class${isInsurance() ? ' d-none' : ''}">${escapeHtml(u.classification)}</td>
                <td class="col-review-service${isInsurance() ? ' d-none' : ''}">${escapeHtml(u.service)}</td>
                <td class="col-review-days${!isInsurance() ? ' d-none' : ''}">${escapeHtml(u.numberOfDays)}</td>
                <td class="col-review-price${!isInsurance() ? ' d-none' : ''}">${escapeHtml(u.price)}</td>
                <td>${u.active ? `<span class="badge bg-success">${locYes()}</span>` : `<span class="badge bg-secondary">${locNo()}</span>`}</td>
            `;
            reviewTbody.appendChild(tr);
        });
    }

    // ── Submit ────────────────────────────────────────
    submitBtn.addEventListener('click', async () => {
        const filled = filledRows();
        if (!filled.length) return;
        submitBtn.disabled = true;

        const payload = {
            offerId: currentOfferId,
            offerGroupName: currentOfferGroup,
            units: filled.map(u => {
                const groupData: Record<string, string> = {};
                if (isInsurance()) {
                    if (u.numberOfDays) groupData['NumberOfDays'] = u.numberOfDays;
                    if (u.price) groupData['Price'] = u.price;
                    if (agencyAcceptsPartnership && u.partnerPriceOverride) groupData['PartnerPriceOverride'] = u.partnerPriceOverride;
                    else if (u.price && !agencyAcceptsPartnership) groupData['PartnerPriceOverride'] = u.price;
                }
                return {
                    offerId: currentOfferId,
                    name: u.name,
                    offerUnitTypeId: u.offerUnitTypeId,
                    classification: u.classification,
                    service: u.service,
                    active: u.active,
                    offerGroupName: currentOfferGroup,
                    groupData,
                };
            }),
        };

        try {
            const result = await postJson(config.urlCreateUnits, payload);
            if (result.success) {
                showNotification(`${filled.length} unit(s) created successfully.`, 'success');
                window.bootstrap.Modal.getInstance(unitModal)?.hide();
                location.reload();
            } else {
                showNotification('Failed to create units.', 'error');
                submitBtn.disabled = false;
            }
        } catch {
            showNotification('An error occurred while creating units.', 'error');
            submitBtn.disabled = false;
        }
    });

    // ── Load form data + reset ────────────────────────
    async function openModal(offerId: number, group: string, offerName: string): Promise<void> {
        currentOfferId    = offerId;
        currentOfferGroup = group;
        offerNameEl.textContent = offerName ? `— ${offerName}` : '';

        // Reset steps
        step1El.classList.remove('d-none');
        step2El.classList.add('d-none');
        reviewBtn.classList.remove('d-none');
        submitBtn.classList.add('d-none');
        backBtn.classList.add('d-none');
        step2Indicator?.classList.remove('active');

        // Reset rows — start with one empty row; user can add more with "+ Add row"
        rows = [blankRow()];
        nextId = 2;

        // Load options
        loadingEl.classList.remove('d-none');
        const table = document.getElementById('units-grid');
        if (table) (table as HTMLElement).style.display = 'none';
        addRowBtn.style.display = 'none';

        const data = await loadUnitFormData(config.urlUnitFormData, offerId, formDataCache);
        loadingEl.classList.add('d-none');
        if (table) (table as HTMLElement).style.display = '';
        addRowBtn.style.display = '';

        if (data) {
            agencyAcceptsPartnership = data.agencyAcceptsPartnership !== false;
            // Build option templates from data
            typeOptions    = data.unitTypes.map(t => new Option(t.name, String(t.id)));
            classOptions   = data.classifications.map(c => new Option(c.name, c.key));
            serviceOptions = data.services.map(s => new Option(s.name, s.key));
        }

        applyColumnVisibility();
        renderGrid();
    }

    // ── Trigger: Index page (.btn-open-create-unit) ───
    document.addEventListener('click', async (e) => {
        const btn = (e.target as HTMLElement).closest('.btn-open-create-unit') as HTMLElement | null;
        if (!btn) return;

        const offerId   = parseInt(btn.dataset.offerId ?? '0', 10);
        const group     = btn.dataset.offerGroup ?? '';
        const offerName = btn.dataset.offerName ?? '';

        await openModal(offerId, group, offerName);
        new window.bootstrap.Modal(unitModal).show();
    });

    // ── Trigger: Details page (data-bs-toggle, fixedOfferId) ─
    if (config.fixedOfferId) {
        unitModal.addEventListener('show.bs.modal', async () => {
            await openModal(config.fixedOfferId!, config.fixedOfferGroup ?? '', config.fixedOfferName ?? '');
        });
    }

    unitModal.addEventListener('hidden.bs.modal', () => {
        // Lightweight cleanup — state is reset on next open
        tbody.innerHTML = '';
        reviewTbody.innerHTML = '';
    });
}

